import React from 'react';
import { AMARILLO_RESERVA, GRUPOS_TIPO_CITA, conceptColor, nombreReserva, opcionesTipoCita, valorTipoCita } from '../../lib/citasApi';
import { SelectGroup, SelectItem, SelectLabel, SelectSeparator } from '../ui/select';

/**
 * Opciones de concepto agrupadas por categoría (Consultas / Cirugías) para
 * usar dentro de un <SelectContent>. Todas las citas funcionan igual: la
 * categoría solo organiza el desplegable.
 *
 * RESERVAS (12/10/2026): por cada tipo con la marca «Admite reserva»
 * (Gestión de agendas → Conceptos de cita) sale «Reserva <tipo>», con la
 * primera letra del tipo en minúscula salvo una sigla («Reserva cirugía X»,
 * «Reserva AB1»: `nombreReserva`, 15/10/2026). Su valor es
 * el del tipo con la marca (`valorTipoCita(code, true)`): quien use el
 * selector lo lee con `leerTipoCita`. Qué tipos la llevan es un dato de la
 * clínica, no una lista de aquí.
 *
 * EL ORDEN (administración, 21/10/2026): primero todos los tipos que no son
 * reserva —Consultas y Cirugías— y al final, en su propio grupo «Reservas»,
 * todas las «Reserva …» seguidas: las de consulta y después las de cirugía,
 * cada parte en el orden de siempre. Lo decide `opcionesTipoCita`, la misma
 * función que usa el resumen de la cita. Los rótulos de grupo solo salen si
 * hay más de un grupo.
 *
 * Props:
 *  - conceptos:  lista de conceptos activos
 *  - minutesFor: (concepto) => minutos a mostrar (permite aplicar overrides
 *                de la agenda); si no se pasa, usa default_minutes.
 *  - conReserva: ofrecer las opciones «Reserva <tipo>» (por defecto, sí).
 */
export default function ConceptoOptions({ conceptos = [], minutesFor, conReserva = true }) {
  const minutes = minutesFor || ((c) => c.default_minutes);

  const renderItem = (c, reserva = false) => (
    <SelectItem key={valorTipoCita(c.code, reserva)} value={valorTipoCita(c.code, reserva)}>
      <span className="inline-flex items-center gap-2">
        <span
          className="w-2.5 h-2.5 rounded-full shrink-0"
          style={{ backgroundColor: reserva ? AMARILLO_RESERVA : conceptColor(c) }}
          aria-hidden="true"
        />
        {reserva ? nombreReserva(c.name) : c.name} · {minutes(c)} min
      </span>
    </SelectItem>
  );
  const opciones = opcionesTipoCita(conceptos, { conReserva });
  const grupos = GRUPOS_TIPO_CITA
    .map((g) => ({ ...g, opciones: opciones.filter((o) => o.grupo === g.clave) }))
    .filter((g) => g.opciones.length > 0);

  if (grupos.length <= 1) return <>{opciones.map(({ concepto, reserva }) => renderItem(concepto, reserva))}</>;

  return (
    <>
      {grupos.map((g, i) => (
        <React.Fragment key={g.clave}>
          {i > 0 && <SelectSeparator />}
          <SelectGroup>
            <SelectLabel>{g.etiqueta}</SelectLabel>
            {g.opciones.map(({ concepto, reserva }) => renderItem(concepto, reserva))}
          </SelectGroup>
        </React.Fragment>
      ))}
    </>
  );
}
